import { useEffect } from 'react';

/**
 * Right-click context menu over the AHU rows (B1.4 / parity 140) — faithful
 * port of `fn.uni_tools.attachContextMenuToList` with the EXACT arguments the
 * AhuView passes: container `#main_ahus`, rowSelector
 * `.list-inline.col-xs-12.show_ahu_data, .show_static_version`, buttonSelector
 * `.icon-more-actions.btn-more`, menuSelector `.dropdown-menu`,
 * `project: true`.
 *
 * contextmenu on a row → its dropdown menu gets `.activated`
 * (`.show_ahu_data .dropdown-menu.activated` CSS shows it) positioned at the
 * cursor via `left = min(-165, posX - menuRect.left - menuRect.width + 15)`;
 * the row's parent gets `.z-100`; right-clicking the more-actions button
 * itself is suppressed; any document click hides the menu.
 *
 * The `.top` flip branch reads `$('.admin-table.first-fixed-list')` — absent
 * on this page, so the legacy computation yields NaN and `.top` is only ever
 * REMOVED. Replicated by removing `.top` (no flip), faithful to the live DOM.
 */
export function useAhuContextMenu(
  rootRef: React.RefObject<HTMLElement | null>,
  enabled = true
): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled) return undefined;

    const ROW_SELECTOR =
      '.list-inline.col-xs-12.show_ahu_data, .show_static_version';
    const MENU_SELECTOR = '.dropdown-menu';
    const BUTTON_SELECTOR = '.icon-more-actions.btn-more';

    let activeMenu: HTMLElement | null = null;

    const hideMenu = () => {
      if (activeMenu) {
        activeMenu.classList.remove('activated');
        activeMenu.style.left = '';
        activeMenu = null;
      }
      document
        .querySelectorAll('#main_ahus .z-100')
        .forEach((el) => el.classList.remove('z-100'));
    };

    const showMenu = (menu: HTMLElement, x: number, row: HTMLElement) => {
      menu.classList.add('activated');
      menu.classList.remove('top');
      const rect = menu.getBoundingClientRect();
      // project=true branch positioning.
      menu.style.left = `${Math.min(-165, x - rect.left - rect.width + 15)}px`;
      document
        .querySelectorAll('#main_ahus .z-100')
        .forEach((el) => el.classList.remove('z-100'));
      row.parentElement?.classList.add('z-100');
      // tableSelector '.admin-table.first-fixed-list' is absent here → the
      // legacy NaN comparison only ever removes `.top`.
      menu.classList.remove('top');
    };

    const onContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest(BUTTON_SELECTOR)) return;
      const row = target.closest(ROW_SELECTOR) as HTMLElement | null;
      if (!row || !root.contains(row)) return;
      const menu = row.querySelector(MENU_SELECTOR) as HTMLElement | null;
      if (!menu) return;
      e.preventDefault();
      hideMenu();
      activeMenu = menu;
      showMenu(menu, e.pageX, row);
    };

    const onClickOutside = () => hideMenu();

    root.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('click', onClickOutside);
    return () => {
      root.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('click', onClickOutside);
      hideMenu();
    };
  }, [rootRef, enabled]);
}
